// (2026-05-23) Vista HTML responsive de la factura publica (escaneo QR).
// Reemplaza el viejo iframe-con-PDF que no rendia en moviles. Renderiza
// los mismos datos del modal "Detalles de la Factura" pero como pagina
// responsive optimizada para celulares. Mantiene un boton "Descargar PDF"
// que dispara la generacion del PDF cliente-side bajo demanda.
import React, { useState } from "react";
import { generateInvoicesPDF } from "../utils/pdf/InvoicesPDF/generateInvoicesPDF";
// (2026-07-22 Viajes) Fuente ÚNICA de los 15 campos del nodo `/viajes`, la misma
// que usan el formulario, las vistas de detalle y el PDF: así las cuatro
// superficies no pueden divergir en nombres, etiquetas ni orden.
import { VIAJE_CAMPOS } from "../utils/viajeCampos";

const fmt = (n, decimals = 2) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString("es-VE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const fmtFecha = (s) => {
  // (2026-05-23 fix bug #2) Cuando el backend devuelve una fecha en formato
  // "YYYY-MM-DD" (sin hora), `new Date("2026-05-22")` la interpreta como
  // medianoche UTC. En zona horaria America/Caracas (UTC-4) esa medianoche
  // UTC es "2026-05-21 20:00:00 VET", y `toLocaleDateString("es-VE")`
  // devolvia 21/05/2026 — un dia antes. Para evitarlo, si la cadena es
  // YYYY-MM-DD parseamos los componentes manualmente y los formateamos sin
  // pasar por Date, asi no hay shift de zona horaria.
  if (!s) return "—";
  try {
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("es-VE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return s; }
};

const tipoDocLabel = (t) => ({
  FC: "FACTURA", NC: "NOTA DE CRÉDITO", ND: "NOTA DE DÉBITO",
}[t] || (t || "DOCUMENTO"));

// (2026-08-02, ítem 32-ii/iii) Total de la línea en la vista pública.
//
// El serializador público YA calcula bien el total en Bs
// (`total = subtotal - descuento + recargo`, documento_generation_routes.py:
// :472), pero `total_aplicada` lo LEE de la columna persistida cuando existe
// (:469-471) y esa columna se escribe en la conversión proforma -> factura
// IGNORANDO el descuento por monto fijo y el recargo (factura_routes.py:
// :2299-2305). Por eso, cuando la línea trae uno de esos dos importes, el total
// se recalcula a partir de sus componentes en la MISMA moneda que se muestra;
// en cualquier otro caso se respeta el valor del backend (ningún documento que
// hoy se ve bien cambia).
// (H6, auditoría 2026-08-02) DESCUENTO EFECTIVO de la línea en la vista pública.
//
// La columna "Desc." enlazaba solo `monto_descuento`/`descuento_aplicada`, que
// son la columna `descuento_monto` del ítem — y esa columna vale 0 en el
// descuento por PORCENTAJE, que es el único tipo que existía históricamente. El
// resultado: la línea mostraba "0,00" de descuento mientras el total SÍ lo
// descontaba. Afirmar 0,00 habiendo descuento es peor que no decir nada.
//
// El payload público NO expone `descuento_porcentaje` (documento_generation_
// routes.py:486-503), así que el descuento del porcentaje se DERIVA de sus
// componentes:
//   - Con monto > 0 se usa el monto (descuento por monto fijo).
//   - En Bs, `total` lo calcula el serializador FRESCO
//     (`subtotal - descuento + recargo`, :472) => descuento = subtotal - total + recargo.
//   - En moneda aplicada, `total_aplicada` se LEE de la columna persistida
//     (:469-471), que la conversión escribe ignorando descuento por monto y
//     recargo (factura_routes.py:2299-2305): la derivación solo es fiable si la
//     línea NO tiene recargo. Con recargo se devuelve `null` = NO DETERMINABLE,
//     y la celda muestra "—" en vez de inventar una cifra.
// Devuelve un número >= 0, o null cuando no se puede determinar.
const descuentoItemPublico = (it, esVES) => {
  if (!it) return 0;
  const n = (v) => (v === null || v === undefined || v === "" ? 0 : (Number(v) || 0));
  const monto = esVES ? n(it.monto_descuento) : n(it.descuento_aplicada || it.monto_descuento);
  if (monto > 0) return monto;
  const recargo = esVES ? n(it.recargo_monto) : n(it.recargo_monto_aplicada || it.recargo_monto);
  const precio = esVES ? n(it.precio_unitario) : n(it.precio_unitario_aplicada || it.precio_unitario);
  const subtotal = n(it.cantidad) * precio;
  const total = esVES ? n(it.total) : n(it.total_aplicada != null ? it.total_aplicada : it.total);
  if (subtotal <= 0) return 0;
  if (!esVES && recargo > 0) return null; // no determinable con el dato publicado
  const derivado = esVES ? (subtotal - total + recargo) : (subtotal - total);
  if (derivado <= 0.005) return 0;
  return Math.round(derivado * 100) / 100;
};

const totalItemPublico = (it, esVES) => {
  if (!it) return 0;
  const n = (v) => (v === null || v === undefined || v === "" ? 0 : (Number(v) || 0));
  const descuento = esVES ? n(it.monto_descuento) : n(it.descuento_aplicada || it.monto_descuento);
  const recargo = esVES ? n(it.recargo_monto) : n(it.recargo_monto_aplicada || it.recargo_monto);
  const almacenado = esVES ? it.total : (it.total_aplicada != null ? it.total_aplicada : it.total);
  if (descuento <= 0 && recargo <= 0) return n(almacenado);
  const precio = esVES ? n(it.precio_unitario) : n(it.precio_unitario_aplicada || it.precio_unitario);
  const subtotal = n(it.cantidad) * precio;
  return Math.round((subtotal - descuento + recargo) * 100) / 100;
};

const PublicInvoiceView = ({ data }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  if (!data) return null;

  const emisor    = data.emisor || {};
  const tercero   = data.tercero || null;
  const receptor  = data.receptor || {};
  const moneda    = data.moneda || {};
  const items     = data.items || [];
  const pagos     = data.pagos_detalle || [];
  const totales   = data.totales || {};
  const documento = data.documento || {};
  const legal     = data.legal || {};

  const sim = moneda.simbolo || (moneda.codigo === "USD" ? "$" : moneda.codigo === "EUR" ? "€" : "Bs.");
  const cod = (moneda.codigo || "VES").toUpperCase();
  const esVES = cod === "VES";
  const tipoDoc = documento.tipo_documento || "FC";

  const handleDownload = async () => {
    try {
      setDownloading(true); setError("");
      const id = documento.qr_token || documento.id;
      await generateInvoicesPDF(id, "download");
    } catch (e) {
      setError(e?.message || "No se pudo generar el PDF.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="piv-root">
      {/* Header de marca */}
      <div className="piv-topbar">
        {/* (2026-05-26 v17) URL relativa — mismo dominio que el frontend */}
        <img src="/quantus-logo.png" alt="Quantus Invoice" className="piv-topbar-logo" />
        <span className="piv-topbar-by">by Sirius Solutions</span>
      </div>

      <div className="piv-page">
        {/* (2026-07-08) Banner "DOCUMENTO ANULADO" — el serializer envía
            documento.estatus ('normal' | 'anulada'). Rojo, prominente, arriba. */}
        {documento.estatus === "anulada" && (
          <div style={{
            background: "#dc2626", color: "#fff", textAlign: "center",
            fontWeight: 800, letterSpacing: "0.15em", padding: "10px 16px",
            borderRadius: "10px", margin: "0 0 16px", fontSize: "18px",
          }}>
            DOCUMENTO ANULADO
            {documento.fecha_anulacion && (
              <div style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.04em", marginTop: "4px" }}>
                Anulado el {documento.fecha_anulacion}
              </div>
            )}
          </div>
        )}
        {/* Caja roja — Nº de Control SENIAT */}
        {/* (#117 Increment 1b) Usa `documento.numero_control` (ex-`serial`,
            formato XX-NNNNNNNN del lote SENIAT segun Providencia 102). El
            numero_control legacy per-empresa fue ELIMINADO; ya no existe la clave
            `serial`. Coincide con el PDF (caja roja arriba). */}
        <div className="piv-seniat">
          <span>Nro. de control SENIAT:</span>
          <strong>{documento.numero_control || "—"}</strong>
        </div>

        {/* Cabecera: Emisor + Datos del documento */}
        <div className="piv-grid-2">
          <div className="piv-card">
            <div className="piv-card-title">EMISOR</div>
            <div className="piv-card-body">
              <div className="piv-strong">{emisor.nombre_empresa || "—"}</div>
              <div className="piv-muted">RIF: {emisor.rif || "—"}</div>
              {emisor.denominacion_comercial && (
                <div className="piv-small">{emisor.denominacion_comercial}</div>
              )}
              {emisor.direccion_fiscal && (
                <div className="piv-small">{emisor.direccion_fiscal}</div>
              )}
              {emisor.telefono && (
                <div className="piv-small">Tel: {emisor.telefono}</div>
              )}
              {emisor.email && (
                <div className="piv-small">{emisor.email}</div>
              )}
            </div>
          </div>

          <div className="piv-card piv-card-doc">
            <div className="piv-card-title">{tipoDocLabel(tipoDoc)}</div>
            <div className="piv-card-body">
              <div className="piv-kv"><span>Nº Documento</span><strong>{documento.numero_documento || documento.numero_factura || "—"}</strong></div>
              <div className="piv-kv"><span>Nº Control</span><strong>{documento.numero_control || "—"}</strong></div>
              <div className="piv-kv"><span>Fecha emisión</span><strong>{fmtFecha(documento.fecha_emision)}</strong></div>
              {documento.hora_emision && (
                <div className="piv-kv"><span>Hora</span><strong>{documento.hora_emision}</strong></div>
              )}
              <div className="piv-kv"><span>Moneda</span><strong>{cod}</strong></div>
              {!esVES && totales.tipo_cambio_bcv && (
                <div className="piv-kv"><span>Tasa BCV</span><strong>{fmt(totales.tipo_cambio_bcv, 4)} Bs.</strong></div>
              )}
            </div>
          </div>
        </div>

        {/* Cliente */}
        <div className="piv-card">
          <div className="piv-card-title">CLIENTE</div>
          <div className="piv-card-body piv-grid-3">
            <div>
              <div className="piv-muted-label">Razón social</div>
              <div className="piv-strong">{receptor.nombre || "—"}</div>
            </div>
            <div>
              <div className="piv-muted-label">Documento</div>
              <div>RIF: {receptor.rif || "—"}</div>
            </div>
            <div>
              <div className="piv-muted-label">Teléfono</div>
              <div>{receptor.telefono || "—"}</div>
            </div>
            <div>
              <div className="piv-muted-label">Correo</div>
              <div className="piv-break">{receptor.correo || "—"}</div>
            </div>
            <div className="piv-col-span-2">
              <div className="piv-muted-label">Dirección</div>
              <div>{receptor.direccion || "—"}</div>
            </div>
          </div>
        </div>

        {/* Tercero (si aplica) */}
        {tercero && (
          <div className="piv-card piv-card-tercero">
            <div className="piv-card-title">TERCERO</div>
            <div className="piv-card-body piv-grid-3">
              <div>
                <div className="piv-muted-label">Razón social</div>
                <div className="piv-strong">{tercero.nombre_empresa || "—"}</div>
              </div>
              <div>
                <div className="piv-muted-label">RIF</div>
                <div>{tercero.rif || "—"}</div>
              </div>
              {tercero.telefono && (
                <div>
                  <div className="piv-muted-label">Teléfono</div>
                  <div>{tercero.telefono}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="piv-card">
          <div className="piv-card-title">DETALLE</div>
          <div className="piv-items-desktop">
            <table className="piv-items-table">
              <thead>
                <tr>
                  <th>Cod.</th>
                  <th>Descripción</th>
                  <th className="piv-right">Cant.</th>
                  <th className="piv-right">P. Unit. ({sim})</th>
                  <th className="piv-right">% IVA</th>
                  <th className="piv-right">Desc.</th>
                  {/* (2026-08-02, ítem 32-ii) El recargo de línea ya viajaba en
                      el payload público (recargo_monto/_aplicada) y ninguna
                      superficie lo mostraba. */}
                  <th className="piv-right">Recargo</th>
                  <th className="piv-right">Total ({sim})</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={8} className="piv-empty">Sin líneas en este documento.</td></tr>
                )}
                {items.map((it, i) => (
                  <tr key={i}>
                    <td>{it.producto?.codigo || ""}</td>
                    <td style={{ whiteSpace: "pre-wrap" }}>{it.descripcion || ""}</td>
                    <td className="piv-right">{fmt(it.cantidad)}</td>
                    <td className="piv-right">{fmt(esVES ? it.precio_unitario : (it.precio_unitario_aplicada || it.precio_unitario))}</td>
                    <td className="piv-right">{(it.porcentaje_iva || "")}</td>
                    {/* (H6) Descuento EFECTIVO (monto fijo o % derivado); "—"
                        cuando no hay descuento o no es determinable. */}
                    <td className="piv-right">{(() => {
                      const d = descuentoItemPublico(it, esVES);
                      return (d === null || d <= 0) ? "—" : fmt(d);
                    })()}</td>
                    <td className="piv-right">{(() => {
                      const r = Number(esVES ? it.recargo_monto : (it.recargo_monto_aplicada || it.recargo_monto)) || 0;
                      return r > 0 ? fmt(r) : "—";
                    })()}</td>
                    <td className="piv-right piv-total-cell">{fmt(totalItemPublico(it, esVES))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Vista móvil: tarjeta por item */}
          <div className="piv-items-mobile">
            {items.map((it, i) => (
              <div key={i} className="piv-item-card">
                <div className="piv-item-head">
                  <span className="piv-muted-label">{it.producto?.codigo || ""}</span>
                  <strong>{fmt(totalItemPublico(it, esVES))} {sim}</strong>
                </div>
                {it.descripcion && (
                  <div className="piv-item-desc">{it.descripcion}</div>
                )}
                <div className="piv-item-meta">
                  <span>Cant. <strong>{fmt(it.cantidad)}</strong></span>
                  <span>P.U. <strong>{fmt(esVES ? it.precio_unitario : (it.precio_unitario_aplicada || it.precio_unitario))} {sim}</strong></span>
                  <span>IVA <strong>{it.porcentaje_iva || ""}</strong></span>
                  {/* (ítem 32-ii) Descuento y recargo de línea también en móvil:
                      la tarjeta muestra el total de la línea, y sin estos dos
                      importes ese total no se puede explicar. (H6) El descuento
                      es el EFECTIVO — incluye el del porcentaje. */}
                  {(descuentoItemPublico(it, esVES) || 0) > 0 && (
                    <span>Desc. <strong>{fmt(descuentoItemPublico(it, esVES))} {sim}</strong></span>
                  )}
                  {Number(esVES ? it.recargo_monto : (it.recargo_monto_aplicada || it.recargo_monto)) > 0 && (
                    <span>Recargo <strong>{fmt(esVES ? it.recargo_monto : (it.recargo_monto_aplicada || it.recargo_monto))} {sim}</strong></span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* (2026-07-22 Viajes) Nodo `/viajes` — datos de pasajero y boleto.
            Se muestran aquí por decisión del cliente: esta es la vista que ve
            quien escanea el QR del documento, incluido un fiscal. Si el PDF los
            imprime y esta vista no, el mismo documento contaría dos cosas
            distintas según por dónde se mire. Render ESTRICTAMENTE condicional:
            un documento sin boletos se ve exactamente igual que antes. */}
        {(data.viajes || []).length > 0 && (
          <div className="piv-viajes">
            <h3 className="piv-seccion-titulo">
              {data.es_boleteria_aerea ? "Boletería aérea" : "Datos del viaje"}
            </h3>
            {data.viajes.map((viaje, idx) => (
              <div key={idx} className="piv-viaje">
                <div className="piv-viaje-titulo">
                  Boleto #{viaje.numero_linea || idx + 1}
                </div>
                <div className="piv-viaje-campos">
                  {VIAJE_CAMPOS.map((campo) => (
                    viaje[campo.key] ? (
                      <div className="piv-kv" key={campo.key}>
                        <span>{campo.label}</span><strong>{viaje[campo.key]}</strong>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Totales */}
        <div className="piv-totales">
          {/* (H7, auditoría 2026-08-02) QUÉ SUMA CADA SUPERFICIE — discrepancia
              PREEXISTENTE de agregados, documentada sin cambiar cifras: aquí se
              imprime el AGREGADO de cabecera `totales.total_recargos(_aplicada)`
              (el mismo que el PDF), mientras las modales de detalle suman las
              FILAS HIJAS `recargos_globales`. Con datos coherentes ambos
              coinciden; si divergieran, la diferencia está en el backend
              (cabecera vs hijas), no en estas vistas. */}
          {/* (2026-08-02, ítem 32-i) RECARGOS globales. El serializador público
              NO expone la lista {descripcion, monto} — solo el agregado
              `total_recargos`/`_aplicada` (documento_generation_routes.py:
              :964-967), que es también lo que imprime el PDF ("Recargo Total").
              Se muestra solo si hay recargo: la base ya lo lleva prorrateado y
              sin esta fila el importe no se puede explicar. */}
          {Number(esVES ? totales.total_recargos : totales.total_recargos_aplicada) > 0 && (
            <div className="piv-total-row">
              <span>Recargos</span>
              <strong>{fmt(esVES ? totales.total_recargos : totales.total_recargos_aplicada)} {sim}</strong>
            </div>
          )}
          {/* (2026-08-03, P3) DESCUENTOS. Igual que en recargos, el serializador
              público NO expone la lista {descripcion, monto} — solo el agregado
              `total_descuento`/`_aplicada` (documento_generation_routes.py), que
              desde P3 suma los descuentos de LÍNEA **y** los GLOBALES (antes solo
              los de línea), y es también lo que imprime el PDF. Se muestra solo
              si hay descuento: sin esta fila la base rebajada no se puede
              explicar. Condicional -> documentos sin descuento se ven igual. */}
          {Number(esVES ? totales.total_descuento : totales.total_descuento_aplicada) > 0 && (
            <div className="piv-total-row">
              <span>Descuentos</span>
              <strong>{fmt(esVES ? totales.total_descuento : totales.total_descuento_aplicada)} {sim}</strong>
            </div>
          )}
          <div className="piv-total-row"><span>Base imponible</span><strong>{fmt(esVES ? totales.total_base : totales.total_base_aplicada)} {sim}</strong></div>
          <div className="piv-total-row"><span>I.V.A.</span><strong>{fmt(esVES ? totales.total_impuestos : totales.total_impuestos_aplicada)} {sim}</strong></div>
          {totales.igtf_porcentaje > 0 && (
            <div className="piv-total-row"><span>IGTF ({fmt(totales.igtf_porcentaje, 0)}%)</span><strong>{fmt(esVES ? totales.igtf_monto : totales.igtf_monto_aplicada)} {sim}</strong></div>
          )}
          {/* (2026-05-23 v6 fix bug #3) Usar `total_a_pagar` del backend
              (nuevo campo desde v6) = total_factura + IGTF. Cuando IGTF=0
              coincide con total_factura. Fallback al cálculo manual si el
              backend es viejo y no expone el campo. Etiqueta ahora dice
              "Total a Pagar" para alinearse con SENIAT (el TOTAL anterior
              era ambiguo). */}
          <div className="piv-total-row piv-total-final">
            <span>Total a Pagar</span>
            <strong>{fmt(
              esVES
                ? (totales.total_a_pagar != null && totales.total_a_pagar !== ""
                    ? totales.total_a_pagar
                    : Number(totales.total_factura || 0) + Number(totales.igtf_monto || 0))
                : (totales.total_a_pagar_aplicada != null && totales.total_a_pagar_aplicada !== ""
                    ? totales.total_a_pagar_aplicada
                    : Number(totales.total_factura_aplicada || 0) + Number(totales.igtf_monto_aplicada || 0))
            )} {sim}</strong>
          </div>
        </div>

        {/* Pagos */}
        {pagos.length > 0 && (
          <div className="piv-card">
            <div className="piv-card-title">PAGOS</div>
            <div className="piv-card-body">
              <table className="piv-pagos-table">
                <thead>
                  <tr>
                    <th>Método</th>
                    <th>Banco</th>
                    <th className="piv-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((p, i) => (
                    <tr key={i}>
                      <td>{p.metodo_nombre || p.metodo_codigo}</td>
                      <td>{p.banco || "—"}</td>
                      <td className="piv-right">{fmt(p.monto)} {p.moneda?.codigo === "USD" ? "$" : p.moneda?.codigo === "EUR" ? "€" : "Bs."}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Coletilla fiscal informativa (IGTF/RLIVA). Viene ya resuelta del
            backend en legal.coletilla_fiscal (snapshot del lote o texto
            estándar), en su caso natural. Antes gateaba por legal.leyenda_legal,
            clave que el serializador nunca envía -> no se mostraba nunca. */}
        {legal.coletilla_fiscal && (
          <div className="piv-legal">
            {legal.coletilla_fiscal}
          </div>
        )}

        {/* Acciones */}
        <div className="piv-actions">
          <button onClick={handleDownload} disabled={downloading} className="piv-btn-pdf">
            {downloading ? "Generando PDF…" : "Descargar PDF"}
          </button>
          {error && <div className="piv-error">{error}</div>}
        </div>

        {/* Footer marca */}
        <div className="piv-footer">
          Documento autorizado por SENIAT — emitido a través de <strong>Quantus Invoice</strong>.
          <br />
          <span className="piv-muted-label">Powered by Sirius Solutions</span>
        </div>
      </div>

      <style>{`
        .piv-root {
          background: #f8fafc; min-height: 100vh; color: #0f172a;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        }
        .piv-topbar {
          background: #0f172a; padding: 12px 16px;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .piv-topbar-logo { height: 28px; width: auto; }
        .piv-topbar-by { color: #cbd5e1; font-size: 11px; letter-spacing: 0.04em; }
        .piv-page { max-width: 760px; margin: 0 auto; padding: 16px; }
        .piv-seniat {
          background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;
          padding: 12px 16px; margin-bottom: 14px;
          display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
          gap: 8px;
        }
        .piv-seniat span { font-size: 13px; font-weight: 600; color: #0f172a; }
        .piv-seniat strong { font-size: 18px; color: #dc2626; letter-spacing: 0.04em; }
        .piv-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
        @media (max-width: 600px) { .piv-grid-2 { grid-template-columns: 1fr; } }
        .piv-card {
          background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px;
          margin-bottom: 14px; overflow: hidden;
        }
        .piv-card-title {
          background: #1e293b; color: #cbd5e1; padding: 8px 14px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
        }
        .piv-card-doc .piv-card-title { background: #dc2626; color: #fff; }
        .piv-card-tercero .piv-card-title { background: #7c3aed; color: #fff; }
        .piv-card-body { padding: 14px; font-size: 13px; line-height: 1.6; }
        .piv-strong { font-weight: 700; font-size: 14px; }
        .piv-muted { color: #64748b; }
        .piv-small { color: #475569; font-size: 12px; }
        .piv-muted-label { color: #64748b; font-size: 11px; letter-spacing: 0.04em; text-transform: uppercase; }
        .piv-break { word-break: break-all; }
        .piv-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
        .piv-col-span-2 { grid-column: span 2; }
        @media (max-width: 600px) {
          .piv-grid-3 { grid-template-columns: 1fr 1fr; }
          .piv-col-span-2 { grid-column: span 2; }
        }
        .piv-kv {
          display: flex; justify-content: space-between; gap: 8px;
          padding: 2px 0;
        }
        .piv-kv span { color: #64748b; font-size: 12px; }
        .piv-kv strong { font-weight: 600; }
        .piv-items-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .piv-items-table th {
          background: #f1f5f9; color: #0f172a; text-align: left;
          padding: 8px 10px; font-weight: 600; border-bottom: 1px solid #e2e8f0;
        }
        .piv-items-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .piv-right { text-align: right; }
        .piv-total-cell { font-weight: 700; }
        .piv-empty { padding: 16px; text-align: center; color: #94a3b8; font-style: italic; }
        .piv-items-mobile { display: none; padding: 6px 10px 10px; }
        @media (max-width: 600px) {
          .piv-items-desktop { display: none; }
          .piv-items-mobile { display: block; }
        }
        .piv-item-card {
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;
          padding: 10px 12px; margin-bottom: 8px;
        }
        .piv-item-head { display: flex; justify-content: space-between; align-items: baseline; }
        .piv-item-desc { font-size: 13px; margin: 6px 0; white-space: pre-wrap; }
        .piv-item-meta { display: flex; gap: 12px; flex-wrap: wrap; font-size: 12px; color: #475569; }
        .piv-totales {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
          padding: 14px 16px; margin-bottom: 14px;
        }
        .piv-total-row {
          display: flex; justify-content: space-between; padding: 6px 0;
          font-size: 14px;
        }
        .piv-total-row span { color: #475569; }
        .piv-total-row strong { font-weight: 700; }
        .piv-total-final {
          margin-top: 8px; padding-top: 10px; border-top: 2px solid #1e293b;
          font-size: 17px;
        }
        .piv-total-final strong { color: #dc2626; }
        .piv-pagos-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .piv-pagos-table th { background: #f1f5f9; padding: 8px 10px; text-align: left; font-weight: 600; border-bottom: 1px solid #e2e8f0; }
        .piv-pagos-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; }
        .piv-legal {
          background: #fffbeb; border: 1px solid #fde68a; color: #713f12;
          border-radius: 8px; padding: 12px 14px; font-size: 12px; margin-bottom: 14px;
          line-height: 1.6;
        }
        .piv-actions {
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          margin: 20px 0;
        }
        .piv-btn-pdf {
          background: #1e293b; color: #fff; border: none; cursor: pointer;
          padding: 12px 28px; border-radius: 8px; font-size: 14px; font-weight: 600;
          letter-spacing: 0.02em; box-shadow: 0 2px 6px rgba(0,0,0,0.12);
          transition: transform 120ms ease, background 120ms ease;
        }
        .piv-btn-pdf:hover:not(:disabled) { background: #0f172a; transform: translateY(-1px); }
        .piv-btn-pdf:disabled { opacity: 0.6; cursor: wait; }
        .piv-error { color: #dc2626; font-size: 12px; }
        .piv-footer {
          text-align: center; font-size: 11px; color: #64748b;
          padding: 16px; margin-top: 16px; border-top: 1px solid #e2e8f0;
          background: #fff;
        }
      `}</style>
    </div>
  );
};

export default PublicInvoiceView;
